/* Becker reviews (2026-10-19): opens the review form, shrinks photos in the browser (a re-saved photo carries no
   location data), sends the review to the reviews Worker, shows more reviews, and opens photos larger.
   The reviews themselves are in the page; nothing here is needed to read them. */
(() => {
  const root = document.querySelector('[data-bk-reviews]');
  if (!root) return;

  const $ = (sel) => root.querySelector(sel);
  const endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
  const sitekey = root.dataset.sitekey || '';
  const productId = root.dataset.productId;
  const perPage = Number(root.dataset.perPage) || 6;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const MAX_PHOTOS = 3;
  const MAX_BYTES = 10 * 1024 * 1024;
  const EDGE = 2000;
  const MSG = {
    rating: 'Choose 1 to 5 stars.',
    body: 'Write a few words about the candle.',
    name: 'Add your name.',
    email: 'Check your email address.',
    photoType: 'That photo did not open, so try a JPG or PNG.',
    photoMax: 'Add up to 3 photos.',
    check: 'The robot check above the button is still working, so please try again in a moment.',
    send: 'We could not send your review just now, so please try again in a minute.',
    sending: 'Sending your review.',
    photos: 'Getting your photos ready.',
    thanks: 'Thank you. Your review is in.',
  };

  const write = $('[data-bk-write]');
  const wrap = $('[data-bk-rform]');
  const form = $('[data-bk-form]');
  const done = $('[data-bk-done]');
  const doneText = $('[data-bk-done-text]');
  const status = $('[data-bk-status]');
  const send = $('[data-bk-send]');
  const starset = $('.bk-rform__starset');
  const filesInput = $('[data-bk-files]');
  const addLabel = $('[data-bk-add]');
  const thumbs = $('[data-bk-thumbs]');
  const tsBox = $('[data-bk-turnstile]');

  let photos = []; // { blob, url }
  let token = '';
  let widget = null;
  let busy = false;

  /* ---------- open and close ---------- */
  function open(scroll) {
    if (!wrap || !write) return;
    wrap.hidden = false;
    write.setAttribute('aria-expanded', 'true');
    loadTurnstile();
    if (scroll) {
      wrap.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      const first = form.querySelector('input[name="rating"]');
      if (first) first.focus({ preventScroll: true });
    }
  }
  if (write && wrap) {
    write.addEventListener('click', () => {
      if (wrap.hidden) open(true);
      else { wrap.hidden = true; write.setAttribute('aria-expanded', 'false'); }
    });
    const fromLink = () => { if (location.hash === '#write-a-review') open(true); };
    if (location.hash === '#write-a-review') window.addEventListener('load', () => setTimeout(fromLink, 60), { once: true });
    window.addEventListener('hashchange', fromLink);
  }

  /* ---------- the robot check, loaded only when the form opens ---------- */
  function loadTurnstile() {
    if (widget !== null || !sitekey || !tsBox) return;
    widget = 'loading';
    window.bkTurnstileReady = () => {
      widget = window.turnstile.render(tsBox, {
        sitekey, theme: 'light', size: 'flexible', action: 'review',
        callback: (t) => {
          token = t;
          const el = $('[data-bk-err="form"]');
          if (el && el.textContent === MSG.check) clearError('form'); // only its own message, never a send failure
        },
        'expired-callback': () => { token = ''; },
        'error-callback': () => { token = ''; },
      });
    };
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=bkTurnstileReady';
    s.async = true;
    document.head.appendChild(s);
  }
  function resetTurnstile() {
    token = '';
    if (window.turnstile && widget && widget !== 'loading') window.turnstile.reset(widget);
  }
  function waitForToken(ms) {
    return new Promise((resolve) => {
      const started = Date.now();
      (function tick() {
        if (token || Date.now() - started > ms) resolve(token);
        else setTimeout(tick, 200);
      })();
    });
  }

  /* ---------- errors ---------- */
  const fieldOf = (name) => (name === 'rating' ? null : form && form.elements[name]);
  function showError(name, text) {
    const el = $(`[data-bk-err="${name}"]`) || $('[data-bk-err="form"]');
    if (!el) return;
    el.textContent = text;
    el.hidden = false;
    const f = fieldOf(name);
    if (f && f.setAttribute) f.setAttribute('aria-invalid', 'true');
  }
  function clearError(name) {
    const el = $(`[data-bk-err="${name}"]`);
    if (el) { el.hidden = true; el.textContent = ''; }
    const f = fieldOf(name);
    if (f && f.removeAttribute) f.removeAttribute('aria-invalid');
  }
  function focusFirstError() {
    const err = [...root.querySelectorAll('[data-bk-err]')].find((el) => !el.hidden);
    if (!err) return;
    const name = err.dataset.bkErr;
    const target = name === 'rating' ? form.querySelector('input[name="rating"]') : fieldOf(name);
    (target && target.focus ? target : send).focus();
  }

  /* ---------- stars ---------- */
  if (form) {
    form.addEventListener('change', (e) => {
      if (e.target.name === 'rating') { starset.dataset.value = e.target.value; clearError('rating'); }
    });
    form.addEventListener('input', (e) => { if (e.target.name) clearError(e.target.name); });
  }

  /* ---------- photos ---------- */
  function viaImg(file) {
    return new Promise((resolve, reject) => {
      const u = URL.createObjectURL(file);
      const i = new Image();
      i.onload = () => { URL.revokeObjectURL(u); resolve(i); };
      i.onerror = () => { URL.revokeObjectURL(u); reject(new Error('load')); };
      i.src = u;
    });
  }
  function decode(file) {
    if ('createImageBitmap' in window) return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => viaImg(file));
    return viaImg(file);
  }
  async function shrink(file) {
    const img = await decode(file);
    const scale = Math.min(1, EDGE / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    c.getContext('2d').drawImage(img, 0, 0, w, h);
    if (img.close) img.close();
    return new Promise((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/jpeg', 0.85));
  }
  window.bkShrinkPhoto = shrink; // used by the page checks
  function drawThumbs() {
    thumbs.textContent = '';
    photos.forEach((p, i) => {
      const li = document.createElement('li');
      li.className = 'bk-rform__thumb';
      const img = document.createElement('img');
      img.src = p.url;
      img.alt = `Your photo ${i + 1}`;
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.setAttribute('aria-label', `Remove photo ${i + 1}`);
      rm.textContent = '×';
      rm.addEventListener('click', () => {
        URL.revokeObjectURL(p.url);
        photos.splice(i, 1);
        drawThumbs();
        (addLabel && !addLabel.hidden ? filesInput : send).focus();
      });
      li.append(img, rm);
      thumbs.append(li);
    });
    const full = photos.length >= MAX_PHOTOS;
    addLabel.hidden = full;
    filesInput.disabled = full;
  }
  if (filesInput) {
    filesInput.addEventListener('change', async () => {
      clearError('photos');
      const picked = Array.from(filesInput.files || []);
      filesInput.value = '';
      for (const file of picked) {
        if (photos.length >= MAX_PHOTOS) { showError('photos', MSG.photoMax); break; }
        try {
          const blob = await shrink(file);
          if (blob.size > MAX_BYTES) throw new Error('size');
          photos.push({ blob, url: URL.createObjectURL(blob) });
        } catch {
          showError('photos', MSG.photoType);
        }
      }
      drawThumbs();
    });
  }

  /* ---------- sending ---------- */
  function call(path, body, ms) {
    const opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) };
    if (window.AbortSignal && AbortSignal.timeout) opts.signal = AbortSignal.timeout(ms);
    return fetch(endpoint + path, opts);
  }
  async function uploadPhotos() {
    const res = await call('/v1/uploads', { files: photos.map((p, i) => ({ name: `photo-${i + 1}.jpg`, type: 'image/jpeg', size: p.blob.size })) }, 20000);
    if (!res.ok) throw new Error('uploads');
    const { targets } = await res.json();
    const urls = [];
    for (let i = 0; i < targets.length; i++) {
      const fd = new FormData();
      targets[i].parameters.forEach((p) => fd.append(p.name, p.value));
      fd.append('file', photos[i].blob, `photo-${i + 1}.jpg`);
      const up = await fetch(targets[i].url, { method: 'POST', body: fd });
      if (!up.ok) throw new Error('upload');
      urls.push(targets[i].resourceUrl);
    }
    return urls;
  }
  function collect() {
    const checked = form.querySelector('input[name="rating"]:checked');
    return {
      rating: checked ? Number(checked.value) : 0,
      title: form.elements.title.value.trim(),
      body: form.elements.body.value.trim(),
      name: form.elements.name.value.trim(),
      email: form.elements.email.value.trim(),
    };
  }
  function check(d) {
    ['rating', 'body', 'name', 'email', 'photos', 'form'].forEach(clearError);
    let ok = true;
    if (!(d.rating >= 1 && d.rating <= 5)) { showError('rating', MSG.rating); ok = false; }
    if (d.body.length < 2) { showError('body', MSG.body); ok = false; }
    if (!d.name) { showError('name', MSG.name); ok = false; }
    if (!/^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(d.email)) { showError('email', MSG.email); ok = false; }
    if (!ok) focusFirstError();
    return ok;
  }
  function say(text) { if (status) status.textContent = text; }
  function finish(message) {
    form.hidden = true;
    done.hidden = false;
    doneText.textContent = message || MSG.thanks;
    if (write) write.hidden = true;
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    done.focus();
  }

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (busy) return;
      const d = collect();
      if (!check(d)) return;
      busy = true;
      send.disabled = true;
      form.setAttribute('aria-busy', 'true');
      try {
        if (!token) {
          say(MSG.sending);
          await waitForToken(8000);
          if (!token) { showError('form', MSG.check); return; }
        }
        let urls = [];
        if (photos.length) { say(MSG.photos); urls = await uploadPhotos(); }
        say(MSG.sending);
        const res = await call('/v1/reviews', { product_id: productId, ...d, photos: urls, turnstile: token, website: form.elements.website.value }, 30000);
        const out = await res.json().catch(() => ({}));
        if (res.ok) { finish(out.message); return; }
        if (res.status === 400 && out.errors) {
          Object.entries(out.errors).forEach(([k, m]) => showError(['rating', 'title', 'body', 'name', 'email', 'photos'].includes(k) ? k : 'form', m));
          focusFirstError();
        } else {
          showError('form', out.message || MSG.send);
        }
        resetTurnstile();
      } catch {
        showError('form', MSG.send);
        resetTurnstile();
      } finally {
        busy = false;
        send.disabled = false;
        form.removeAttribute('aria-busy');
        say('');
      }
    });
  }

  /* ---------- show more ---------- */
  const more = $('[data-bk-more]');
  if (more) {
    more.addEventListener('click', () => {
      const later = [...root.querySelectorAll('[data-bk-later][hidden]')];
      const next = later.slice(0, perPage);
      next.forEach((li) => { li.hidden = false; });
      if (next[0]) next[0].focus();
      if (later.length <= perPage) more.hidden = true;
    });
  }

  /* ---------- photos, larger ---------- */
  const dlg = $('[data-bk-dialog]');
  const dlgImg = $('[data-bk-dialog-img]');
  let opener = null;
  if (dlg && typeof dlg.showModal === 'function') {
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bk-photo]');
      if (!btn) return;
      opener = btn;
      dlgImg.src = btn.dataset.bkPhoto;
      const label = (btn.getAttribute('aria-label') || '').replace(/^Open /, '');
      dlgImg.alt = label.charAt(0).toUpperCase() + label.slice(1);
      dlg.showModal();
    });
    $('[data-bk-dialog-close]').addEventListener('click', () => dlg.close());
    dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', () => { dlgImg.removeAttribute('src'); if (opener) opener.focus(); });
  }
})();
